import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import supabase from '../supabase';

const ImageUpload = ({ value, onChange, placeholder = "Drop image here or click to upload" }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(value);
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  // Handle file upload to Supabase Storage
  const handleFile = useCallback(async (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      alert('File size too large. Please select an image under 10MB');
      return;
    }

    setIsUploading(true);

    try {
      // Test storage connection first
      console.log('🔍 Testing storage connection...');
      
      // Try to list files in bucket to test access
      const { data: testData, error: testError } = await supabase.storage
        .from('event-images')
        .list('', { limit: 1 });
      
      if (testError) {
        console.error('Storage test failed:', testError);
        throw testError;
      }
      
      console.log('✅ Storage connection successful');
      
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `event_${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;

      console.log('📤 Uploading file:', fileName);

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('event-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Upload error details:', error);
        console.error('Error message:', error.message);
        console.error('Error details:', error.details);
        
        // If bucket doesn't exist, try to create it (this might fail due to permissions)
        if (error.message.includes('Bucket not found')) {
          alert('Storage bucket not found. Using base64 fallback...');
          // Fallback to base64
          const reader = new FileReader();
          reader.onload = (e) => {
            const dataUrl = e.target.result;
            setPreview(dataUrl);
            onChange(dataUrl);
            setIsUploading(false);
          };
          reader.readAsDataURL(file);
          return;
        }

        // If it's a permissions error
        if (error.message.includes('not allowed') || error.message.includes('permission') || error.message.includes('policy')) {
          alert('Storage permissions not configured. Using base64 fallback...\n\nTo fix: Set up Row Level Security policies for the event-images bucket in Supabase.');
          // Fallback to base64
          const reader = new FileReader();
          reader.onload = (e) => {
            const dataUrl = e.target.result;
            setPreview(dataUrl);
            onChange(dataUrl);
            setIsUploading(false);
          };
          reader.readAsDataURL(file);
          return;
        }
        
        throw error;
      }

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('event-images')
        .getPublicUrl(data.path);

      const publicUrl = publicUrlData.publicUrl;
      
      setPreview(publicUrl);
      onChange(publicUrl);
      setIsUploading(false);

    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file. Please try again or use a URL instead.');
      setIsUploading(false);
    }
  }, [onChange]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = async () => {
    // If it's a Supabase storage URL, try to delete the file
    if (preview && preview.includes('supabase.co/storage')) {
      try {
        const urlParts = preview.split('/');
        const fileName = urlParts[urlParts.length - 1];
        
        await supabase.storage
          .from('event-images')
          .remove([fileName]);
        
        console.log('✅ Deleted file from storage:', fileName);
      } catch (error) {
        console.warn('Failed to delete file from storage:', error);
        // Continue anyway - just a cleanup attempt
      }
    }
    
    setPreview(null);
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUrlInput = (e) => {
    const url = e.target.value;
    setPreview(url);
    onChange(url);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* URL Input */}
      <div>
        <label style={{ 
          display: 'block', 
          marginBottom: '0.5rem', 
          fontWeight: '600', 
          color: 'white',
          fontSize: '0.9rem'
        }}>
          Image URL (or upload file below)
        </label>
        <input
          type="url"
          value={value || ''}
          onChange={handleUrlInput}
          style={{
            width: '100%',
            padding: '0.75rem',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'white',
            fontSize: '1rem',
            boxSizing: 'border-box'
          }}
          placeholder="https://example.com/image.jpg"
        />
      </div>

      {/* Upload Area */}
      <div
        ref={dropRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        style={{
          border: `2px dashed ${isDragging ? '#00ff80' : 'rgba(255, 255, 255, 0.3)'}`,
          borderRadius: '12px',
          padding: '2rem',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragging ? 'rgba(0, 255, 128, 0.1)' : 'rgba(255, 255, 255, 0.05)',
          transition: 'all 0.3s ease',
          position: 'relative',
          minHeight: '120px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem'
        }}
      >
        {isUploading ? (
          <>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid rgba(0, 255, 128, 0.3)',
              borderTop: '3px solid #00ff80',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <span style={{ color: '#00ff80', fontWeight: '600' }}>Uploading...</span>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </>
        ) : preview ? (
          <div style={{ position: 'relative', width: '100%' }}>
            <img
              src={preview}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '200px',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div style={{ display: 'none', color: '#9ca3af' }}>
              <ImageIcon size={48} />
              <div>Image failed to load</div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'rgba(239, 68, 68, 0.8)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)'
              }}
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <>
            <Upload size={48} style={{ color: isDragging ? '#00ff80' : '#9ca3af' }} />
            <div style={{ 
              color: isDragging ? '#00ff80' : 'white',
              fontWeight: '600',
              fontSize: '1.1rem'
            }}>
              {placeholder}
            </div>
            <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
              Files uploaded to Supabase Storage • JPG, PNG, GIF up to 10MB
            </div>
          </>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
};

export default ImageUpload;
